import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Search,
  Building,
  Phone,
  PhoneOff,
  Eye,
  RotateCcw,
  Trash2,
  Loader2,
  CheckSquare,
  Square,
  MinusSquare
} from 'lucide-react';
import { leadsAPI } from '../services/api';
import {
  formatName,
  formatPhone,
  formatCompany,
  formatDate
} from '../utils/formatters';
import ConfirmationModal from '../components/ConfirmationModal';

const DoNotCallList = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Selection State
  const [selectedLeads, setSelectedLeads] = useState([]);

  // Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    leadId: null,
    leadName: ''
  });
  const [restoreModal, setRestoreModal] = useState({
    isOpen: false,
    leadId: null,
    leadName: ''
  });
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkRestoreModal, setBulkRestoreModal] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await leadsAPI.getLeads({
        status: 'do_not_call',
        search: searchTerm,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });
      setLeads(response.data.leads || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error fetching do not call leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLeads();
  };

  const handleRestore = async () => {
    try {
      await leadsAPI.updateLead(restoreModal.leadId, { status: 'new' });
      toast.success('Lead restored to active leads');
      setRestoreModal({ isOpen: false, leadId: null, leadName: '' });
      fetchLeads();
    } catch (error) {
      console.error('Error restoring lead:', error);
      toast.error('Failed to restore lead');
    }
  };

  const handleDelete = async () => {
    try {
      await leadsAPI.deleteLead(deleteModal.leadId);
      toast.success('Lead deleted permanently');
      setDeleteModal({ isOpen: false, leadId: null, leadName: '' });
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead._id));
    }
  };

  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Bulk operations
  const handleBulkRestore = async () => {
    if (selectedLeads.length === 0) return;

    setIsBulkProcessing(true);
    try {
      await leadsAPI.bulkAction({
        action: 'updateStatus',
        leadIds: selectedLeads,
        data: { status: 'new' }
      });
      toast.success(`${selectedLeads.length} lead(s) restored successfully`);
      setSelectedLeads([]);
      setBulkRestoreModal(false);
      fetchLeads();
    } catch (error) {
      console.error('Error bulk restoring leads:', error);
      toast.error('Failed to restore leads');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;

    setIsBulkProcessing(true);
    try {
      await leadsAPI.bulkAction({
        action: 'delete',
        leadIds: selectedLeads
      });
      toast.success(`${selectedLeads.length} lead(s) deleted permanently`);
      setSelectedLeads([]);
      setBulkDeleteModal(false);
      fetchLeads();
    } catch (error) {
      console.error('Error bulk deleting leads:', error);
      toast.error('Failed to delete leads');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Clear selection when leads change
  useEffect(() => {
    setSelectedLeads([]);
  }, [leads]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <PhoneOff className="h-8 w-8 text-red-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Do Not Call List</h1>
              <p className="text-gray-500">
                {pagination.total} lead{pagination.total !== 1 ? 's' : ''} marked as Do Not Call
              </p>
            </div>
          </div>
        </div>

        {/* Search and Bulk Actions */}
        <div className="mb-6 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, company, phone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Search
            </button>
          </form>

          {/* Bulk Actions */}
          {leads.length > 0 && (
            <div className="flex items-center gap-4 p-3 bg-gray-100 rounded-lg">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
              >
                {selectedLeads.length === leads.length ? (
                  <CheckSquare className="h-5 w-5 text-primary-600" />
                ) : selectedLeads.length > 0 ? (
                  <MinusSquare className="h-5 w-5 text-primary-600" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
                {selectedLeads.length === leads.length
                  ? 'Deselect All'
                  : selectedLeads.length > 0
                  ? `${selectedLeads.length} Selected`
                  : 'Select All'}
              </button>

              {selectedLeads.length > 0 && (
                <>
                  <div className="h-6 w-px bg-gray-300" />
                  <button
                    onClick={() => setBulkRestoreModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Restore Selected ({selectedLeads.length})
                  </button>
                  <button
                    onClick={() => setBulkDeleteModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Selected ({selectedLeads.length})
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <PhoneOff className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leads in Do Not Call list</h3>
              <p className="mt-1 text-sm text-gray-500">
                Leads marked as "Do Not Call" will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <button onClick={toggleSelectAll} className="p-1 hover:bg-gray-200 rounded">
                        {selectedLeads.length === leads.length ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : selectedLeads.length > 0 ? (
                          <MinusSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <motion.tr
                      key={lead._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`hover:bg-gray-50 ${selectedLeads.includes(lead._id) ? 'bg-primary-50' : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleSelectLead(lead._id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {selectedLeads.includes(lead._id) ? (
                            <CheckSquare className="h-5 w-5 text-primary-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatName(lead.firstName, lead.lastName)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Building className="h-4 w-4 mr-1" />
                          {formatCompany(lead.company)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Phone className="h-4 w-4 mr-1" />
                          {formatPhone(lead.phone)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(lead.updatedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/leads/${lead._id}`}
                            className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => setRestoreModal({
                              isOpen: true,
                              leadId: lead._id,
                              leadName: formatName(lead.firstName, lead.lastName)
                            })}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                            title="Restore to Leads"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteModal({
                              isOpen: true,
                              leadId: lead._id,
                              leadName: formatName(lead.firstName, lead.lastName)
                            })}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete Permanently"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Restore Modal */}
      <ConfirmationModal
        isOpen={restoreModal.isOpen}
        onClose={() => setRestoreModal({ isOpen: false, leadId: null, leadName: '' })}
        onConfirm={handleRestore}
        title="Restore Lead"
        message={`Are you sure you want to restore "${restoreModal.leadName}" to active leads? They will appear in the main leads list again.`}
        confirmText="Restore"
        confirmButtonClass="bg-green-600 hover:bg-green-700"
      />

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, leadId: null, leadName: '' })}
        onConfirm={handleDelete}
        title="Delete Lead Permanently"
        message={`Are you sure you want to permanently delete "${deleteModal.leadName}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Bulk Restore Modal */}
      <ConfirmationModal
        isOpen={bulkRestoreModal}
        onClose={() => setBulkRestoreModal(false)}
        onConfirm={handleBulkRestore}
        title="Restore Selected Leads"
        message={`Are you sure you want to restore ${selectedLeads.length} lead(s) to active leads? They will appear in the main leads list again.`}
        confirmText={isBulkProcessing ? 'Restoring...' : 'Restore All'}
        confirmButtonClass="bg-green-600 hover:bg-green-700"
      />

      {/* Bulk Delete Modal */}
      <ConfirmationModal
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Selected Leads Permanently"
        message={`Are you sure you want to permanently delete ${selectedLeads.length} lead(s)? This action cannot be undone.`}
        confirmText={isBulkProcessing ? 'Deleting...' : 'Delete All'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default DoNotCallList;
